import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import validator from "validator";
import AuthBrandPanel from "components/Auth/AuthBrandPanel";
import AuthField from "components/Auth/AuthField";
import { InlineFormMessage } from "components/InlineFeedback/InlineFeedback";
import arrowLeft from "assets/images/auth/arrow-left.svg";
import emailIcon from "assets/images/auth/email-field-icon.svg";
import successCheck from "assets/images/auth/success-check.svg";
import { forgotUserPassword } from "utils/authApi";
import { showResMsg } from "utils/generic";
import "styles/auth.css";

function ResetPassword() {
  const dispatch = useDispatch();
  const [emailAddress, setEmailAddress] = useState("");
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  const requestReset = (email) => {
    setIsSubmitting(true);
    setFormError("");
    forgotUserPassword(
      { emailAddress: email },
      (data) => {
        setIsSubmitting(false);
        if (!data.Result_Status) {
          setFormError(data.Result_Message || "Failed to send reset link.");
          return;
        }
        setSentEmail(data.Result_Output?.emailAddress || email);
      },
      dispatch
    );
  };

  const submitReset = (event) => {
    event.preventDefault();
    setFormError("");
    const email = emailAddress.trim();
    if (!email) {
      setEmailError("Email address can’t be blank");
      return;
    }
    if (!validator.isEmail(email)) {
      setEmailError("Enter a valid email address");
      return;
    }
    requestReset(email);
  };

  if (sentEmail) {
    return (
      <main className="auth-page auth-page--confirmation">
        <AuthBrandPanel />
        <section className="auth-panel auth-panel--confirmation">
          <div className="auth-confirmation">
            <span className="auth-success-ring">
              <img src={successCheck} alt="" />
            </span>
            <div className="auth-confirmation__title">
              <h2>Check your email</h2>
              <p>
                We&apos;ve sent a password reset link to{" "}
                <strong>{sentEmail}</strong>
              </p>
            </div>
            {formError && (
              <InlineFormMessage
                message={formError}
                type="error"
                onClose={() => setFormError("")}
              />
            )}
            <div className="auth-confirmation__actions">
              <button
                className={`auth-button auth-button--primary${
                  isSubmitting ? " auth-button--loading" : ""
                }`}
                type="button"
                disabled={isSubmitting}
                onClick={() => requestReset(sentEmail)}
              >
                {isSubmitting && <span className="auth-button__spinner" />}
                <span>{isSubmitting ? "Sending..." : "Resend email"}</span>
              </button>
              <p>
                Didn&apos;t receive the email? Check your spam folder or try
                again.
              </p>
            </div>
            <div className="auth-divider" />
            <Link className="auth-back-link" to="/login">
              <img src={arrowLeft} alt="" />
              Back to sign in
            </Link>
          </div>
          <footer className="auth-legal">
            <a href="#privacy">Privacy</a>
            <span>·</span>
            <a href="#terms">Terms</a>
          </footer>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page auth-page--reset">
      <AuthBrandPanel />
      <section className="auth-panel auth-panel--reset">
        <form
          className="auth-form auth-form--reset"
          onSubmit={submitReset}
          noValidate
        >
          <header className="auth-title">
            <h2>Reset your password</h2>
            <p>Enter your email and we&apos;ll send you a reset link</p>
          </header>
          {formError && (
            <InlineFormMessage
              message={formError}
              type="error"
              onClose={() => setFormError("")}
            />
          )}
          <AuthField
            id="reset-email"
            label="Email address"
            required
            error={emailError}
          >
            <div className="auth-email-control">
              <img src={emailIcon} alt="" />
              <input
                id="reset-email"
                className={`auth-control${
                  emailError ? " auth-control--error" : ""
                }`}
                type="email"
                value={emailAddress}
                onChange={(event) => {
                  setEmailAddress(event.target.value);
                  setEmailError("");
                }}
                placeholder="you@example.com.au"
                maxLength={50}
                autoComplete="email"
              />
            </div>
          </AuthField>
          <div className="auth-actions auth-actions--reset">
            <button
              className={`auth-button auth-button--primary${
                isSubmitting ? " auth-button--loading" : ""
              }`}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting && <span className="auth-button__spinner" />}
              <span>
                {isSubmitting ? "Sending link..." : "Send reset link"}
              </span>
            </button>
            <Link className="auth-back-link" to="/login">
              <img src={arrowLeft} alt="" />
              Back to sign in
            </Link>
          </div>
        </form>
        <footer className="auth-legal">
          <a href="#privacy">Privacy</a>
          <span>·</span>
          <a href="#terms">Terms</a>
        </footer>
      </section>
    </main>
  );
}

export default ResetPassword;
